import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import LongRentalServicePicker from '../components/longRent/LongRentalServicePicker';
import PurchaseRecoveryCard from '../components/purchase/PurchaseRecoveryCard';
import PurchaseTermsModal from '../components/purchase/PurchaseTermsModal';
import { CountryMark, SmsIcon } from '../components/sms/SmsMarks';
import { useBrowserVoiceLine } from '../components/voice/useBrowserVoiceLine';
import {
  ApiError,
  cancelVoiceVerification,
  createClientOperationKey,
  createVoiceCallAccess,
  createVoiceVerificationQuote,
  fetchVoiceVerificationCatalog,
  fetchVoiceVerificationOrder,
  fetchVoiceVerifications,
  getApiErrorMessage,
  purchaseVoiceVerification,
  reconcileVoiceVerificationOrder,
  syncVoiceVerification,
  VoiceVerificationSelection,
} from '../services/smsApi';
import {
  VoiceVerificationCatalog,
  VoiceVerificationNumber,
  VoiceVerificationPurchaseOrder,
  VoiceVerificationQuote,
  VoiceVerificationState,
} from '../types/models';
import { formatUsd } from '../utils/format';
import {
  getCodeFromApiError,
  getOrderFromApiError,
  PurchaseRecoveryRecord,
  readPurchaseRecovery,
  savePurchaseRecovery,
} from '../utils/purchaseRecovery';

const RECOVERY_STORAGE_KEY = 'sms:voice:pending-order:v1';

const STATE_LABELS: Record<VoiceVerificationState, string> = {
  pending: 'Ожидает звонка', completed: 'Завершена', canceled: 'Отменена',
  timed_out: 'Время истекло', reported: 'Отправлена жалоба', refunded: 'Возврат',
  reused: 'Использована повторно', reactivated: 'Активирована повторно', unknown: 'Уточняется',
};

function locationLabel(stateName: string | null, areaCode: string | null): string {
  if (areaCode) return `${stateName || 'США'} · ${areaCode}`;
  if (stateName) return `${stateName} · любой код`;
  return 'Любой штат и код';
}

function timeLeft(endsAt: string, now: number): string {
  const seconds = Math.max(0, Math.ceil((new Date(endsAt).getTime() - now) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

const VoiceVerification: React.FC = () => {
  const [catalog, setCatalog] = useState<VoiceVerificationCatalog | null>(null);
  const [serviceInput, setServiceInput] = useState('');
  const [serviceCode, setServiceCode] = useState<string | null>(null);
  const [stateName, setStateName] = useState<string | null>(null);
  const [areaCode, setAreaCode] = useState<string | null>(null);
  const [quote, setQuote] = useState<VoiceVerificationQuote | null>(null);
  const [items, setItems] = useState<VoiceVerificationNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [recoveryRecord, setRecoveryRecord] = useState<PurchaseRecoveryRecord | null>(() => readPurchaseRecovery(RECOVERY_STORAGE_KEY));
  const [recoveryOrder, setRecoveryOrder] = useState<VoiceVerificationPurchaseOrder | null>(null);
  const [checkingRecovery, setCheckingRecovery] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const requestVersion = useRef(0);

  const selection = (nextServiceCode = serviceCode, nextState = stateName, nextArea = areaCode): VoiceVerificationSelection => ({
    serviceCode: nextServiceCode,
    stateName: nextState,
    areaCode: nextArea,
  });

  const selectedLocation = useMemo(
    () => catalog?.locations.find((location) => location.stateName === stateName) || null,
    [catalog, stateName],
  );
  const selectedService = useMemo(
    () => catalog?.services.find((service) => service.code === serviceCode) || null,
    [catalog, serviceCode],
  );

  const loadCatalog = async (nextSelection: VoiceVerificationSelection, initial = false) => {
    const version = ++requestVersion.current;
    initial ? setLoading(true) : setRefreshing(true);
    setError('');
    setQuote(null);
    try {
      const next = await fetchVoiceVerificationCatalog(nextSelection);
      if (version === requestVersion.current) setCatalog(next);
    } catch (loadError) {
      if (version === requestVersion.current) setError(getApiErrorMessage(loadError));
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  const loadItems = async (syncPending = false) => {
    try {
      let next = await fetchVoiceVerifications();
      if (syncPending) {
        const pending = next.filter((item) => item.state === 'pending');
        if (pending.length) {
          await Promise.allSettled(pending.map((item) => syncVoiceVerification(item.id)));
          next = await fetchVoiceVerifications();
        }
      }
      setItems(next);
    } catch (loadError) {
      setError(getApiErrorMessage(loadError));
    }
  };

  const { lineId, lineState, prepareLine, acceptCall, finishCall, sendDigit } = useBrowserVoiceLine(
    createVoiceCallAccess,
    setError,
    () => { void loadItems(true); },
  );

  useEffect(() => {
    void Promise.all([loadCatalog({}, true), loadItems(false)]);
    // Загрузка выполняется один раз; дальше каталогом управляют фильтры.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let timer = 0;
    const tick = () => {
      setNow(Date.now());
      timer = window.setTimeout(tick, 1000);
    };
    timer = window.setTimeout(tick, 1000);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!items.some((item) => item.state === 'pending')) return undefined;
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      await loadItems(true);
      if (!stopped) timer = window.setTimeout(poll, 5000);
    };
    timer = window.setTimeout(poll, 5000);
    return () => { stopped = true; window.clearTimeout(timer); };
    // Перезапуск цепочки нужен только при изменении наличия pending-номеров.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.some((item) => item.state === 'pending')]);

  useEffect(() => {
    if (!recoveryRecord?.orderId) return undefined;
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      try {
        const order = await fetchVoiceVerificationOrder(recoveryRecord.orderId!);
        if (stopped) return;
        setRecoveryOrder(order);
        if (order.verification) {
          setItems((current) => [order.verification!, ...current.filter((item) => item.id !== order.verification?.id)]);
          savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
          setRecoveryRecord(null);
          return;
        }
        if (order.terminal) return;
      } catch (pollError) {
        if (stopped) return;
        if (pollError instanceof ApiError && pollError.status === 404) {
          savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
          setRecoveryRecord(null);
          return;
        }
      }
      if (!stopped) timer = window.setTimeout(poll, 10000);
    };
    void poll();
    return () => { stopped = true; window.clearTimeout(timer); };
  }, [recoveryRecord?.orderId]);

  const changeServiceInput = (value: string) => {
    setServiceInput(value);
    if (serviceCode) {
      setServiceCode(null);
      void loadCatalog(selection(null));
    }
  };

  const selectService = (service: { code: string; name: string }) => {
    setServiceInput('');
    setServiceCode(service.code);
    void loadCatalog(selection(service.code));
  };

  const changeState = (value: string) => {
    const next = value || null;
    setStateName(next);
    setAreaCode(null);
    void loadCatalog(selection(serviceCode, next, null));
  };

  const changeArea = (value: string) => {
    const next = value || null;
    setAreaCode(next);
    void loadCatalog(selection(serviceCode, stateName, next));
  };

  const preparePurchase = async () => {
    if (!serviceCode || !catalog?.offer?.available) return;
    setQuoting(true);
    setError('');
    try {
      setQuote(await createVoiceVerificationQuote(selection()));
    } catch (quoteError) {
      setError(getApiErrorMessage(quoteError));
    } finally {
      setQuoting(false);
    }
  };

  const confirmPurchase = async () => {
    if (!quote || !catalog?.purchasesEnabled || catalog.purchaseReadiness?.ready === false) return;
    setPurchasing(true);
    setError('');
    const operationKey = createClientOperationKey('voice');
    const pendingRecord: PurchaseRecoveryRecord = {
      quoteId: quote.id,
      operationKey,
      orderId: null,
      amount: quote.finalAmount,
      createdAt: new Date().toISOString(),
    };
    savePurchaseRecovery(RECOVERY_STORAGE_KEY, pendingRecord);
    setRecoveryRecord(pendingRecord);
    try {
      const result = await purchaseVoiceVerification(quote.id, operationKey);
      if (result.verification) {
        setItems((current) => [result.verification!, ...current.filter((item) => item.id !== result.verification?.id)]);
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
        setRecoveryRecord(null);
        setRecoveryOrder(null);
        setQuote(null);
      } else {
        const nextRecord = { ...pendingRecord, orderId: result.id };
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, nextRecord);
        setRecoveryRecord(nextRecord);
        setRecoveryOrder(result);
      }
    } catch (purchaseError) {
      const order = getOrderFromApiError(purchaseError) as VoiceVerificationPurchaseOrder | null;
      if (order) {
        const nextRecord = { ...pendingRecord, orderId: order.id };
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, nextRecord);
        setRecoveryRecord(nextRecord);
        setRecoveryOrder(order);
      } else if (getCodeFromApiError(purchaseError)) {
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
        setRecoveryRecord(null);
        setRecoveryOrder(null);
      }
      setError(getApiErrorMessage(purchaseError));
    } finally {
      setPurchasing(false);
    }
  };

  const checkRecovery = async () => {
    if (!recoveryRecord) return;
    setCheckingRecovery(true);
    setError('');
    try {
      const order = recoveryRecord.orderId
        ? await reconcileVoiceVerificationOrder(recoveryRecord.orderId)
        : await purchaseVoiceVerification(recoveryRecord.quoteId, recoveryRecord.operationKey);
      const nextRecord = { ...recoveryRecord, orderId: order.id };
      setRecoveryOrder(order);
      if (order.verification) {
        setItems((current) => [order.verification!, ...current.filter((item) => item.id !== order.verification?.id)]);
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
        setRecoveryRecord(null);
        setQuote(null);
      } else {
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, nextRecord);
        setRecoveryRecord(nextRecord);
      }
    } catch (recoveryError) {
      const order = getOrderFromApiError(recoveryError) as VoiceVerificationPurchaseOrder | null;
      if (order) {
        const nextRecord = { ...recoveryRecord, orderId: order.id };
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, nextRecord);
        setRecoveryRecord(nextRecord);
        setRecoveryOrder(order);
      }
      setError(getApiErrorMessage(recoveryError));
    } finally {
      setCheckingRecovery(false);
    }
  };

  const cancelItem = async (item: VoiceVerificationNumber) => {
    setError('');
    try {
      const updated = await cancelVoiceVerification(item.id, createClientOperationKey('voice-cancel'));
      setItems((current) => current.map((entry) => entry.id === updated.id ? updated : entry));
    } catch (cancelError) {
      if (cancelError instanceof ApiError && cancelError.status === 202) void loadItems(true);
      setError(getApiErrorMessage(cancelError));
    }
  };

  return (
    <div className="sms-pageStack sms-voice">
      <header className="sms-pageIntro sms-longRent__intro">
        <div>
          <h1>Voice verification</h1>
          <p>Разовый номер США для входящего звонка. После выдачи на проверку обычно даётся 5–15 минут в зависимости от сервиса; точный остаток покажет таймер.</p>
        </div>
        <div className="sms-longRent__geo" aria-label="Выбранное гео">
          <CountryMark name="США" />
          <span><small>Гео</small><strong>США</strong></span>
        </div>
      </header>

      {error ? <div className="sms-inlineAlert is-error" role="alert">{error}</div> : null}

      {recoveryRecord ? (
        <PurchaseRecoveryCard
          kindLabel="Voice verification"
          record={recoveryRecord}
          order={recoveryOrder}
          checking={checkingRecovery}
          onCheck={() => void checkRecovery()}
          onDismiss={() => {
            savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
            setRecoveryRecord(null);
            setRecoveryOrder(null);
          }}
        />
      ) : null}

      <section className="sms-card sms-voice__picker" aria-labelledby="voice-picker-title">
        <div className="sms-sectionHead">
          <h2 id="voice-picker-title">Настройте звонок</h2>
          <p>Выберите сервис. Штат и код номера необязательны и могут увеличить цену.</p>
        </div>
        <LongRentalServicePicker
          services={catalog?.services || []}
          selectedCode={serviceCode}
          query={serviceInput}
          voiceOnly
          headingId="voice-service-title"
          helperText="Показаны поддерживаемые Voice-сервисы. Наличие конкретного номера проверим после выбора."
          listLabel="Сервисы разовой голосовой проверки"
          onQueryChange={changeServiceInput}
          onSelect={selectService}
        />

        <section className="sms-longRent__location" aria-labelledby="voice-location-title">
          <div className="sms-longRent__subhead">
            <div>
              <h3 id="voice-location-title">Выберите гео</h3>
              <p>Страна фиксирована. Штат и код номера можно уточнить при необходимости.</p>
            </div>
          </div>
          <div className="sms-longRent__countryRow">
            <CountryMark name="США" />
            <span><small>Страна</small><strong>США</strong></span>
            <span className="sms-longRent__countryOnly">Единственное доступное гео</span>
          </div>
          <div className="sms-longRent__filters">
            <label className="sms-field">
              <span>Штат</span>
              <select className="u2-input" value={stateName || ''} onChange={(event) => changeState(event.target.value)}>
                <option value="">Любой штат</option>
                {(catalog?.locations || []).map((location) => <option key={location.stateName} value={location.stateName}>{location.stateName}</option>)}
              </select>
            </label>
            <label className="sms-field">
              <span>Код номера</span>
              <select className="u2-input" value={areaCode || ''} disabled={!stateName} onChange={(event) => changeArea(event.target.value)}>
                <option value="">{stateName ? 'Любой код штата' : 'Сначала выберите штат'}</option>
                {(selectedLocation?.areaCodes || []).map((code) => <option key={code} value={code}>{code}</option>)}
              </select>
            </label>
          </div>
        </section>

        {catalog?.purchaseReadiness?.ready === false ? <div className="sms-inlineAlert is-info" role="status">{catalog.purchaseReadiness.message}</div> : null}

        {loading ? <div className="sms-longRent__loading" role="status">Загружаем Voice-сервисы…</div> : null}
        {!loading && serviceCode ? (
          <div className={`sms-voice__offer ${catalog?.offer?.available ? 'is-available' : ''}`} aria-live="polite">
            <div>
              <small>{selectedService?.name} · {locationLabel(stateName, areaCode)}</small>
              <strong>{catalog?.offer?.price !== undefined ? formatUsd(catalog.offer.price) : 'Недоступно'}</strong>
              <span>{refreshing ? 'Проверяем…' : catalog?.offer?.available ? `Номеров: ${catalog.offer.availableQuantity}` : (catalog?.offer?.reason || 'Нет свободных номеров')}</span>
            </div>
            <button type="button" data-purchase-trigger="voice" className="u2-btn is-primary" disabled={!catalog?.offer?.available || refreshing || quoting} onClick={() => void preparePurchase()}>{quoting ? 'Фиксируем цену…' : 'Купить'}</button>
          </div>
        ) : null}
        {!loading && !serviceCode ? <div className="sms-voice__hint"><SmsIcon name="search" /><span><strong>Начните с сервиса</strong><small>Цена и наличие появятся после точного выбора из списка.</small></span></div> : null}
      </section>

      {quote ? (
        <PurchaseTermsModal
          title="Условия Voice verification"
          summary={`${quote.service.name} · ${locationLabel(quote.location.stateName, quote.location.areaCode)}`}
          price={formatUsd(quote.finalAmount)}
          returnFocusSelector="[data-purchase-trigger='voice']"
          purchaseDisabled={!catalog?.purchasesEnabled || catalog.purchaseReadiness?.ready === false}
          purchasePending={purchasing}
          onPurchase={() => void confirmPurchase()}
          onCancel={() => setQuote(null)}
        >
          <dl className="sms-longRent__terms">
            <div><dt>Срок проверки</dt><dd>Обычно 5–15 минут после выдачи номера; точный срок зависит от сервиса и появится на таймере.</dd></div>
            <div><dt>Проверка</dt><dd>{quote.terms.verification}</dd></div>
            <div><dt>Списание и возврат</dt><dd>{quote.terms.charging}</dd></div>
            <div><dt>Отмена</dt><dd>{quote.terms.cancellation}</dd></div>
            <div><dt>Номер после звонка</dt><dd>{quote.terms.retention}</dd></div>
          </dl>
          {!catalog?.purchasesEnabled ? <div className="sms-inlineAlert is-info" role="status">Цены и условия уже работают, но реальная оплата пока выключена.</div> : catalog.purchaseReadiness?.ready === false ? <div className="sms-inlineAlert is-info" role="status">{catalog.purchaseReadiness.message}</div> : null}
        </PurchaseTermsModal>
      ) : null}

      <section className="sms-voice__numbers" aria-labelledby="voice-numbers-title">
        <div className="sms-longNumbers__head"><div><h2 id="voice-numbers-title">Мои Voice-номера</h2><p>Активные звонки и их история.</p></div><button type="button" className="u2-btn is-secondary is-sm" onClick={() => void loadItems(true)}>Обновить</button></div>
        {!items.length ? <div className="sms-voice__empty"><SmsIcon name="headphones" /><span><strong>Пока нет Voice-номеров</strong><small>Выберите сервис выше, чтобы узнать цену и наличие.</small></span></div> : null}
        <div className="sms-voice__list">
          {items.map((item) => {
            const activeLine = lineId === item.id;
            const displayedState = activeLine && lineState === 'connected'
              ? 'Звонок подключён'
              : activeLine && lineState === 'ringing'
                ? 'Входящий звонок'
                : STATE_LABELS[item.state];
            return (
              <article className="sms-voiceNumber" key={item.id}>
                <div className="sms-voiceNumber__top">
                  <div><small>{item.service.name} · {locationLabel(item.location.stateName, item.location.areaCode)}</small><strong>{item.phoneNumber}</strong></div>
                  <span className={`sms-voiceNumber__state is-${item.state}`}>{displayedState}</span>
                </div>
                <div className="sms-voiceNumber__body">
                  <div className="sms-voiceNumber__timer"><small>{item.state === 'pending' ? 'Осталось на звонок' : 'Завершено'}</small><strong>{item.state === 'pending' ? timeLeft(item.endsAt, now) : new Date(item.endsAt).toLocaleString('ru-RU')}</strong></div>
                  {item.state === 'pending' ? (
                    <div className="sms-voiceNumber__line">
                      {!activeLine || lineState === 'idle' || lineState === 'ended' ? <button type="button" className="u2-btn is-primary" onClick={() => void prepareLine(item.id)}>Подготовиться к звонку</button> : null}
                      {activeLine && lineState === 'registering' ? <span className="sms-voiceNumber__lineState">Подключаем линию…</span> : null}
                      {activeLine && lineState === 'ready' ? <span className="sms-voiceNumber__lineState is-ready">Линия готова. Запросите звонок в сервисе.</span> : null}
                      {activeLine && lineState === 'ringing' ? <div className="sms-voiceNumber__incoming"><strong>Входящий звонок</strong><button type="button" className="u2-btn is-primary" onClick={acceptCall}>Ответить</button><button type="button" className="u2-btn is-secondary" onClick={finishCall}>Отклонить</button></div> : null}
                      {activeLine && lineState === 'connected' ? <div className="sms-voiceNumber__connected"><strong>Звонок подключён</strong><div className="sms-voiceNumber__keypad" aria-label="Тоновый набор">{['1','2','3','4','5','6','7','8','9','*','0','#'].map((digit) => <button type="button" key={digit} onClick={() => sendDigit(digit)}>{digit}</button>)}</div><button type="button" className="u2-btn is-secondary" onClick={finishCall}>Завершить</button></div> : null}
                    </div>
                  ) : null}
                  <dl><div><dt>Цена</dt><dd>{formatUsd(item.finalAmount)}</dd></div><div><dt>Звонков</dt><dd>{item.calls.length}</dd></div>{item.customerRefundState === 'completed' ? <div><dt>Возврат</dt><dd>Выполнен</dd></div> : null}</dl>
                  <div className="sms-voiceNumber__actions">{item.canCancel ? <button type="button" className="u2-btn is-secondary is-sm" onClick={() => void cancelItem(item)}>Отменить</button> : null}<details><summary>Условия</summary><p>{item.terms.cancellation} {item.terms.retention}</p></details></div>
                </div>
              </article>
            );
          })}
        </div>
        <div className="sms-voice__rentalNote"><SmsIcon name="calendar" /><div><strong>Нужен этот же номер надолго?</strong><p>Разовый Voice-номер не превращается в арендный. Для долгого срока нужно отдельно купить аренду, если для сервиса доступны звонки.</p></div><Link to="/long-term-rent" className="u2-btn is-secondary">К аренде</Link></div>
      </section>
    </div>
  );
};

export default VoiceVerification;
